import React from 'react';
import LandingLayout from '../layouts/LandingLayout';
import { motion } from 'framer-motion';
import { Mail, Linkedin, ExternalLink, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const Contact: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const email = 'hello@citaloo.com';
  const linkedinUrl = 'https://linkedin.com/company/citaloo';

  const copyEmail = () => {
    navigator.clipboard.writeText(email);
    setCopied(true);
    toast.success('Email copied to clipboard!');
    
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  return (
    <LandingLayout>
      <div className="min-h-screen">
        {/* Hero Section */}
        <motion.section 
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="pt-32 pb-16 px-4 md:px-6 max-w-7xl mx-auto"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/70">
            Get in Touch
          </h1>
          <p className="text-xl text-center text-muted-foreground max-w-2xl mx-auto">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </motion.section>

        {/* Contact Options */}
        <motion.section 
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="py-16 px-4 md:px-6"
        >
          <div className="max-w-2xl mx-auto space-y-8">
            {/* Email Card */}
            <motion.div 
              variants={fadeIn}
              className="p-6 rounded-2xl bg-card border border-border/50 hover:border-primary/50 transition-colors duration-300"
            >
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 rounded-xl bg-primary/10 text-primary">
                  <Mail className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold">Email Us</h3>
              </div>
              <p className="text-muted-foreground mb-4">
                Send us an email anytime! We aim to respond within 24 hours.
              </p>
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                <span className="text-foreground font-medium">{email}</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={copyEmail}
                    className="p-2 hover:bg-background rounded-full transition-colors"
                    aria-label="Copy email"
                  >
                    {copied ? 
                      <Check className="h-5 w-5 text-green-500" /> : 
                      <Copy className="h-5 w-5 text-muted-foreground" />
                    }
                  </button>
                  <a
                    href={`mailto:${email}`}
                    className="p-2 hover:bg-background rounded-full transition-colors"
                    aria-label="Send email"
                  >
                    <ExternalLink className="h-5 w-5 text-muted-foreground" />
                  </a>
                </div>
              </div>
            </motion.div>

            {/* LinkedIn Card */}
            <motion.div 
              variants={fadeIn}
              className="p-6 rounded-2xl bg-card border border-border/50 hover:border-primary/50 transition-colors duration-300"
            >
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 rounded-xl bg-primary/10 text-primary">
                  <Linkedin className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold">Connect on LinkedIn</h3>
              </div>
              <p className="text-muted-foreground mb-4">
                Follow us on LinkedIn for updates, tips, and industry insights.
              </p>
              <a
                href={linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full px-4 py-3 rounded-lg bg-muted/50 text-foreground font-medium hover:bg-muted transition-colors"
              >
                Visit LinkedIn Profile
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </motion.div>
          </div>
        </motion.section>

        {/* Additional Info */}
        <motion.section 
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="py-16 px-4 md:px-6 text-center"
        >
          <div className="max-w-2xl mx-auto">
            <p className="text-muted-foreground">
              Our team typically responds within 24 hours during business days.
              For immediate assistance, please check our{' '}
              <Link to="/features" className="text-primary hover:underline">
                documentation
              </Link>
              .
            </p>
          </div>
        </motion.section>
      </div>
    </LandingLayout>
  );
};

export default Contact; 